import React, { useState, useEffect, useCallback } from 'react';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from 'lib/supabase';
import {
  Eye, RefreshCw, X, ChevronDown, ChevronUp, ExternalLink, AlertTriangle, Plus, Trash2, Save, ShieldCheck
} from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatEuro = (cents) => {
  if (cents == null) return '€0,00';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })?.format(cents / 100);
};

const formatDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts)?.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ITALIAN_MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

const monthLabel = (year, month) => `${ITALIAN_MONTHS?.[month - 1]} ${year}`;

const buildLast12Months = () => {
  const now = new Date();
  const result = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    result?.push({ year: d?.getFullYear(), month: d?.getMonth() + 1 });
  }
  return result;
};

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  paid:               { label: 'Pagata',           bg: 'bg-green-100',  text: 'text-green-800' },
  pending:            { label: 'In attesa',         bg: 'bg-blue-100',   text: 'text-blue-800' },
  failed:             { label: 'Fallita',           bg: 'bg-red-100',    text: 'text-red-800' },
  refunded:           { label: 'Rimborsata',        bg: 'bg-gray-100',   text: 'text-gray-700' },
  partially_refunded: { label: 'Rimborso parziale', bg: 'bg-orange-100', text: 'text-orange-800' },
  rolled_over:        { label: 'Rinviata',          bg: 'bg-purple-100', text: 'text-purple-800' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-700' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg?.bg} ${cfg?.text}`}>
      {cfg?.label}
    </span>
  );
};

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-80"><X size={16} /></button>
  </div>
);

// ─── Refund Modal ─────────────────────────────────────────────────────────────

const RefundModal = ({ invoice, onClose, onSuccess }) => {
  const maxRefundable = (invoice?.amount_cents - (invoice?.refunded_amount_cents || 0)) / 100;
  const [amount, setAmount] = useState(maxRefundable?.toFixed(2));
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (!amountCents || amountCents <= 0 || amountCents > maxRefundable * 100) {
      setError('Importo non valido');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { error: fnError } = await supabase?.functions?.invoke('refund-invoice', {
        body: { invoice_id: invoice?.id, amount_cents: amountCents, reason: reason || undefined }
      });
      if (fnError) throw fnError;
      onSuccess(parseFloat(amount));
    } catch (err) {
      setError(err?.message || 'Errore durante il rimborso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">Rimborsa fattura</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Importo rimborso (max {formatEuro(maxRefundable * 100)})
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={maxRefundable}
                value={amount}
                onChange={(e) => setAmount(e?.target?.value)}
                className="w-full pl-7 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (opzionale)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              rows={3}
              placeholder="Motivo del rimborso..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">
              <AlertTriangle size={14} />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Annulla
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Elaborazione...' : 'Conferma rimborso'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Detail Drawer ────────────────────────────────────────────────────────────

const DetailDrawer = ({ invoice, professionalEmail, onClose, onRefund }) => {
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);

  useEffect(() => {
    if (!invoice?.id) return;
    const fetchQuotes = async () => {
      setLoadingQuotes(true);
      try {
        const { data } = await supabase
          ?.from('invoice_quotes')
          ?.select('quote_id, fee_cents, quotes(id, created_at, preview_price, service_requests(title))')
          ?.eq('invoice_id', invoice?.id);
        setQuotes(data || []);
      } catch (err) {
        console.error('Error fetching invoice quotes:', err);
      } finally {
        setLoadingQuotes(false);
      }
    };
    fetchQuotes();
  }, [invoice?.id]);

  const canRefund = invoice?.status === 'paid' || invoice?.status === 'partially_refunded';

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full z-50 bg-white shadow-2xl overflow-y-auto" style={{ width: 600 }}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Dettaglio fattura</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Professional */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-1">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Professionista</p>
            <p className="text-sm font-semibold text-gray-900">{professionalEmail || '—'}</p>
            <p className="text-xs text-gray-400 font-mono">{invoice?.professional_id}</p>
          </div>

          {/* Period & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Periodo</p>
              <p className="text-sm text-gray-900">{monthLabel(invoice?.period_year, invoice?.period_month)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Stato</p>
              <StatusBadge status={invoice?.status} />
            </div>
          </div>

          {/* Amount */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Importo</p>
              <p className="text-base font-bold text-gray-900">{formatEuro(invoice?.amount_cents)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Valuta</p>
              <p className="text-sm text-gray-700 uppercase">{invoice?.currency || 'eur'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Preventivi</p>
              <p className="text-sm text-gray-700">{invoice?.quotes_count}</p>
            </div>
          </div>

          {/* Stripe PaymentIntent */}
          {invoice?.stripe_payment_intent_id && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Stripe PaymentIntent</p>
              <a
                href={`https://dashboard.stripe.com/payments/${invoice?.stripe_payment_intent_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-mono"
              >
                {invoice?.stripe_payment_intent_id}
                <ExternalLink size={13} />
              </a>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Pagata il</p>
              <p className="text-sm text-gray-700">{formatDate(invoice?.paid_at)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Fallita il</p>
              <p className="text-sm text-gray-700">{formatDate(invoice?.failed_at)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Rimborsata il</p>
              <p className="text-sm text-gray-700">{formatDate(invoice?.refunded_at)}</p>
            </div>
          </div>

          {/* Retry info */}
          {(invoice?.retry_count > 0 || invoice?.failure_reason) && (
            <div className="bg-red-50 rounded-xl p-4 space-y-1">
              <p className="text-xs text-red-600 uppercase tracking-wide font-semibold">Info fallimento</p>
              {invoice?.failure_reason && <p className="text-sm text-red-700">Motivo: {invoice?.failure_reason}</p>}
              {invoice?.retry_count > 0 && <p className="text-sm text-red-700">Tentativi: {invoice?.retry_count}</p>}
              {invoice?.next_retry_at && <p className="text-sm text-red-700">Prossimo tentativo: {formatDate(invoice?.next_retry_at)}</p>}
            </div>
          )}

          {/* Refund info */}
          {invoice?.refunded_amount_cents > 0 && (
            <div className="bg-orange-50 rounded-xl p-4 space-y-1">
              <p className="text-xs text-orange-600 uppercase tracking-wide font-semibold">Rimborso</p>
              <p className="text-sm text-orange-700">Importo rimborsato: {formatEuro(invoice?.refunded_amount_cents)}</p>
              {invoice?.refund_reason && <p className="text-sm text-orange-700">Motivo: {invoice?.refund_reason}</p>}
            </div>
          )}

          {/* Quotes list */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-3">Preventivi inclusi</p>
            {loadingQuotes ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : quotes?.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Nessun preventivo collegato</p>
            ) : (
              <div className="space-y-2">
                {quotes?.map((iq) => (
                  <div key={iq?.quote_id} className="bg-gray-50 rounded-lg px-4 py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {iq?.quotes?.service_requests?.title || 'Richiesta senza titolo'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatDate(iq?.quotes?.created_at)} · €{((iq?.quotes?.preview_price) || 0)?.toLocaleString('it-IT')} preventivato
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                      {formatEuro(iq?.fee_cents)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Refund button */}
          {canRefund && (
            <div className="pt-2 border-t border-gray-100">
              <button
                onClick={onRefund}
                className="w-full py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Rimborsa fattura
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Esenzioni Section ────────────────────────────────────────────────────────

const EsenzioniSection = ({ showToast }) => {
  // Part 1 state
  const [firstNValue, setFirstNValue] = useState(0);
  const [firstNInput, setFirstNInput] = useState('0');
  const [exemptCount, setExemptCount] = useState(0);
  const [savingFirstN, setSavingFirstN] = useState(false);

  // Part 2 state
  const [whitelistRows, setWhitelistRows] = useState([]);
  const [loadingWhitelist, setLoadingWhitelist] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newReason, setNewReason] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // ── Fetch Part 1 ──────────────────────────────────────────────────────────

  const fetchFirstN = useCallback(async () => {
    const { data } = await supabase
      ?.from('platform_config')
      ?.select('value')
      ?.eq('key', 'first_n_professionals_free')
      ?.single();
    const val = data?.value != null ? Number(data?.value) : 0;
    setFirstNValue(val);
    setFirstNInput(String(val));
  }, []);

  const fetchExemptCount = useCallback(async () => {
    const { count } = await supabase
      ?.from('users')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('role', 'professionista')
      ?.eq('billing_exempt', true)
      ?.eq('billing_exempt_reason', 'first_250');
    setExemptCount(count || 0);
  }, []);

  // ── Fetch Part 2 ──────────────────────────────────────────────────────────

  const fetchWhitelist = useCallback(async () => {
    setLoadingWhitelist(true);
    try {
      const { data, error } = await supabase
        ?.from('billing_exempt_emails')
        ?.select('id, email, reason, created_at')
        ?.order('created_at', { ascending: false });
      if (error) throw error;
      setWhitelistRows(data || []);
    } catch (err) {
      console.error('Error fetching whitelist:', err);
    } finally {
      setLoadingWhitelist(false);
    }
  }, []);

  useEffect(() => {
    fetchFirstN();
    fetchExemptCount();
    fetchWhitelist();
  }, [fetchFirstN, fetchExemptCount, fetchWhitelist]);

  // ── Save Part 1 ───────────────────────────────────────────────────────────

  const handleSaveFirstN = async () => {
    const num = parseInt(firstNInput, 10);
    if (isNaN(num) || num < 0) {
      showToast('Inserisci un numero valido', 'error');
      return;
    }
    setSavingFirstN(true);
    try {
      const { error } = await supabase
        ?.from('platform_config')
        ?.upsert(
          { key: 'first_n_professionals_free', value: num, updated_at: new Date()?.toISOString() },
          { onConflict: 'key' }
        );
      if (error) throw error;
      setFirstNValue(num);
      showToast('Configurazione salvata', 'success');
      fetchExemptCount();
    } catch (err) {
      showToast(err?.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setSavingFirstN(false);
    }
  };

  // ── Add email to whitelist ────────────────────────────────────────────────

  const handleAddEmail = async () => {
    const email = newEmail?.trim()?.toLowerCase();
    if (!email) {
      showToast('Inserisci un indirizzo email', 'error');
      return;
    }
    setAddingEmail(true);
    try {
      // Insert into billing_exempt_emails
      const { error: insertErr } = await supabase
        ?.from('billing_exempt_emails')
        ?.insert({ email, reason: newReason?.trim() || null });
      if (insertErr) throw insertErr;

      // Find matching professionista user and update billing_exempt
      const { data: userData } = await supabase
        ?.from('users')
        ?.select('id')
        ?.eq('email', email)
        ?.eq('role', 'professionista')
        ?.maybeSingle();
      if (userData?.id) {
        await supabase
          ?.from('users')
          ?.update({ billing_exempt: true, billing_exempt_reason: 'manual_whitelist' })
          ?.eq('id', userData?.id);
      }

      setNewEmail('');
      setNewReason('');
      showToast('Email aggiunta alla whitelist', 'success');
      fetchWhitelist();
    } catch (err) {
      showToast(err?.message || 'Errore durante l\'aggiunta', 'error');
    } finally {
      setAddingEmail(false);
    }
  };

  // ── Remove email from whitelist ───────────────────────────────────────────

  const handleRemoveEmail = async (row) => {
    setRemovingId(row?.id);
    try {
      const { error: deleteErr } = await supabase
        ?.from('billing_exempt_emails')
        ?.delete()
        ?.eq('id', row?.id);
      if (deleteErr) throw deleteErr;

      // Find matching professionista user and clear billing_exempt
      const { data: userData } = await supabase
        ?.from('users')
        ?.select('id')
        ?.eq('email', row?.email)
        ?.eq('role', 'professionista')
        ?.maybeSingle();
      if (userData?.id) {
        await supabase
          ?.from('users')
          ?.update({ billing_exempt: false, billing_exempt_reason: null })
          ?.eq('id', userData?.id);
      }

      showToast('Email rimossa dalla whitelist', 'success');
      fetchWhitelist();
    } catch (err) {
      showToast(err?.message || 'Errore durante la rimozione', 'error');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Section header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3 bg-gray-50">
        <ShieldCheck size={18} className="text-blue-600" />
        <h2 className="text-base font-bold text-gray-900">Esenzioni fatturazione</h2>
      </div>

      <div className="p-6 space-y-8">

        {/* ── PART 1: Primi N professionisti gratuiti ── */}
        <div>
          <h3 className="text-sm font-bold text-gray-800 mb-1">Primi N professionisti gratuiti</h3>
          <p className="text-xs text-gray-500 mb-4">
            I primi N professionisti registrati (per ordine di registrazione) con <code className="bg-gray-100 px-1 rounded text-xs">billing_exempt_reason = 'first_250'</code> sono esenti dalla fatturazione.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex-1 max-w-xs">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Numero massimo professionisti esenti (per ordine di registrazione)
              </label>
              <input
                type="number"
                min="0"
                value={firstNInput}
                onChange={(e) => setFirstNInput(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleSaveFirstN}
              disabled={savingFirstN}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={14} />
              {savingFirstN ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>

          <div className="mt-3 inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2">
            <span className="text-xs text-blue-700 font-medium">
              Professionisti esenti per questa regola:
            </span>
            <span className="text-sm font-bold text-blue-900">
              {exemptCount} / {firstNValue}
            </span>
          </div>
        </div>

        <div className="border-t border-gray-100" />

        {/* ── PART 2: Whitelist email ── */}
        <div>
          <h3 className="text-sm font-bold text-gray-800 mb-1">Whitelist email (esenzione permanente)</h3>
          <p className="text-xs text-gray-500 mb-4">
            I professionisti con email in questa lista sono esenti dalla fatturazione con motivo <code className="bg-gray-100 px-1 rounded text-xs">manual_whitelist</code>.
          </p>

          {/* Add row */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              type="email"
              placeholder="Email professionista..."
              value={newEmail}
              onChange={(e) => setNewEmail(e?.target?.value)}
              onKeyDown={(e) => { if (e?.key === 'Enter') handleAddEmail(); }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              placeholder="Motivo (opzionale)..."
              value={newReason}
              onChange={(e) => setNewReason(e?.target?.value)}
              className="flex-1 sm:max-w-[220px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleAddEmail}
              disabled={addingEmail}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <Plus size={14} />
              {addingEmail ? 'Aggiunta...' : 'Aggiungi'}
            </button>
          </div>

          {/* Whitelist table */}
          {loadingWhitelist ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : whitelistRows?.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400 italic border border-dashed border-gray-200 rounded-lg">
              Nessuna email in whitelist
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Motivo</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Aggiunta il</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {whitelistRows?.map((row) => (
                    <tr key={row?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">{row?.email}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{row?.reason || <span className="italic text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(row?.created_at)}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleRemoveEmail(row)}
                          disabled={removingId === row?.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={12} />
                          {removingId === row?.id ? 'Rimozione...' : 'Rimuovi'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AdminFatturazione = () => {
  const now = new Date();
  const currentYear = now?.getFullYear();
  const currentMonth = now?.getMonth() + 1;

  // Data
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // KPI extra data
  const [unbilledCount, setUnbilledCount] = useState(0);
  const [quoteFeeCents, setQuoteFeeCents] = useState(99);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [emailSearch, setEmailSearch] = useState('');

  // Collapsible months
  const [collapsedMonths, setCollapsedMonths] = useState({});

  // Drawer
  const [drawerInvoice, setDrawerInvoice] = useState(null);
  const [drawerEmail, setDrawerEmail] = useState(null);

  // Refund modal
  const [refundInvoice, setRefundInvoice] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const last12Months = buildLast12Months();

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch invoices with professional email
      const { data: invData, error: invErr } = await supabase
        ?.from('professional_invoices')
        ?.select('*, users!professional_invoices_professional_id_fkey(email)')
        ?.order('period_year', { ascending: false })
        ?.order('period_month', { ascending: false });

      if (invErr) throw invErr;
      setInvoices(invData || []);

      // Fetch unbilled accepted quotes for current month
      const startOfMonth = new Date(currentYear, currentMonth - 1, 1)?.toISOString();
      const endOfMonth = new Date(currentYear, currentMonth, 1)?.toISOString();
      const { count: unbilled } = await supabase
        ?.from('quotes')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('status', 'accepted')
        ?.eq('billed_via_promo', false)
        ?.eq('fee_billed', false)
        ?.gte('created_at', startOfMonth)
        ?.lt('created_at', endOfMonth);
      setUnbilledCount(unbilled || 0);

      // Fetch quote fee
      const { data: feeData } = await supabase
        ?.from('platform_config')
        ?.select('value')
        ?.eq('key', 'quote_fee_cents')
        ?.single();
      if (feeData?.value) setQuoteFeeCents(Number(feeData?.value));
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, [currentYear, currentMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── KPI calculations ───────────────────────────────────────────────────────

  const mrrPaid = invoices
    ?.filter(i => i?.period_year === currentYear && i?.period_month === currentMonth && i?.status === 'paid')
    ?.reduce((s, i) => s + (i?.amount_cents || 0), 0);
  const mrrPaidCount = invoices
    ?.filter(i => i?.period_year === currentYear && i?.period_month === currentMonth && i?.status === 'paid')?.length;

  const pendingTotal = invoices
    ?.filter(i => i?.status === 'pending' || i?.status === 'failed')
    ?.reduce((s, i) => s + (i?.amount_cents || 0), 0);
  const pendingCount = invoices?.filter(i => i?.status === 'pending' || i?.status === 'failed')?.length;

  const refundedTotal = invoices
    ?.filter(i => (i?.refunded_amount_cents || 0) > 0)
    ?.reduce((s, i) => s + (i?.refunded_amount_cents || 0), 0);
  const refundedCount = invoices?.filter(i => (i?.refunded_amount_cents || 0) > 0)?.length;

  // ── Filtered invoices ──────────────────────────────────────────────────────

  const filtered = invoices?.filter(inv => {
    const email = inv?.users?.email || '';
    if (statusFilter && inv?.status !== statusFilter) return false;
    if (periodFilter) {
      const [py, pm] = periodFilter?.split('-')?.map(Number);
      if (inv?.period_year !== py || inv?.period_month !== pm) return false;
    }
    if (emailSearch?.trim() && !email?.toLowerCase()?.includes(emailSearch?.toLowerCase()?.trim())) return false;
    return true;
  });

  // ── Group by period ────────────────────────────────────────────────────────

  const grouped = filtered?.reduce((acc, inv) => {
    const key = `${inv?.period_year}-${String(inv?.period_month)?.padStart(2, '0')}`;
    if (!acc?.[key]) acc[key] = { year: inv?.period_year, month: inv?.period_month, rows: [] };
    acc?.[key]?.rows?.push(inv);
    return acc;
  }, {});

  const sortedGroupKeys = Object.keys(grouped)?.sort((a, b) => b?.localeCompare(a));

  const toggleMonth = (key) => {
    setCollapsedMonths(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  // ── Refund success ─────────────────────────────────────────────────────────

  const handleRefundSuccess = (amount) => {
    setRefundInvoice(null);
    setDrawerInvoice(null);
    showToast(`Rimborso effettuato per €${amount?.toFixed(2)?.replace('.', ',')}`, 'success');
    fetchData();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fatturazione</h1>
          <p className="text-sm text-gray-500 mt-1">Gestione fatture mensili dei professionisti</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {/* MRR */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">MRR mese corrente</p>
            <p className="text-3xl font-bold text-gray-900">{formatEuro(mrrPaid)}</p>
            <p className="text-xs text-gray-400 mt-1">{mrrPaidCount} {mrrPaidCount === 1 ? 'fattura pagata' : 'fatture pagate'}</p>
          </div>

          {/* In sospeso */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">In sospeso</p>
            <p className="text-3xl font-bold text-gray-900">{formatEuro(pendingTotal)}</p>
            <p className="text-xs text-gray-400 mt-1">{pendingCount} {pendingCount === 1 ? 'fattura da incassare' : 'fatture da incassare'}</p>
          </div>

          {/* Da fatturare */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Da fatturare (mese corrente)</p>
            <p className="text-3xl font-bold text-gray-900">{unbilledCount}</p>
            <p className="text-xs text-gray-400 mt-1">preventivi accettati questo mese</p>
            <p className="text-xs text-blue-600 font-medium mt-1">≈ {formatEuro(unbilledCount * quoteFeeCents)}</p>
          </div>

          {/* Rimborsi */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Rimborsi totali</p>
            <p className="text-3xl font-bold text-gray-900">{formatEuro(refundedTotal)}</p>
            <p className="text-xs text-gray-400 mt-1">{refundedCount} {refundedCount === 1 ? 'fattura rimborsata' : 'fatture rimborsate'}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Status filter */}
            <div className="flex items-center gap-1 flex-wrap">
              {[
                { value: '', label: 'Tutte' },
                { value: 'paid', label: 'Pagate' },
                { value: 'pending', label: 'In sospeso' },
                { value: 'failed', label: 'Fallite' },
                { value: 'refunded', label: 'Rimborsate' },
                { value: 'rolled_over', label: 'Rolled-over' },
              ]?.map(opt => (
                <button
                  key={opt?.value}
                  onClick={() => setStatusFilter(opt?.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    statusFilter === opt?.value
                      ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {opt?.label}
                </button>
              ))}
            </div>

            {/* Period dropdown */}
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e?.target?.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Tutti i periodi</option>
              {last12Months?.map(({ year, month }) => (
                <option key={`${year}-${month}`} value={`${year}-${month}`}>
                  {monthLabel(year, month)}
                </option>
              ))}
            </select>

            {/* Email search */}
            <input
              type="text"
              placeholder="Cerca per email..."
              value={emailSearch}
              onChange={(e) => setEmailSearch(e?.target?.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
            />

            {/* Refresh */}
            <button
              onClick={fetchData}
              className="ml-auto p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Aggiorna"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
            Errore nel caricamento: {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filtered?.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-16 text-center">
            <p className="text-gray-400 text-sm">Nessuna fattura trovata con i filtri selezionati.</p>
          </div>
        )}

        {/* Monthly grouped table */}
        {!loading && !error && filtered?.length > 0 && (
          <div className="space-y-4">
            {sortedGroupKeys?.map((key) => {
              const group = grouped?.[key];
              const isCollapsed = collapsedMonths?.[key];
              const groupTotal = group?.rows?.reduce((s, i) => s + (i?.amount_cents || 0), 0);

              return (
                <div key={key} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  {/* Month header */}
                  <button
                    onClick={() => toggleMonth(key)}
                    className="w-full px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left"
                  >
                    <span className="text-sm font-semibold text-gray-800">
                      {monthLabel(group?.year, group?.month)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {group?.rows?.length} {group?.rows?.length === 1 ? 'fattura' : 'fatture'}
                    </span>
                    <span className="text-xs font-semibold text-gray-700">
                      {formatEuro(groupTotal)}
                    </span>
                    <span className="ml-auto text-gray-400">
                      {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </span>
                  </button>

                  {/* Table */}
                  {!isCollapsed && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Professionista</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Preventivi</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Importo</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Stato</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Data pagamento</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Azioni</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {group?.rows?.map((inv) => {
                            const email = inv?.users?.email || '—';
                            const canRefund = inv?.status === 'paid' || inv?.status === 'partially_refunded';
                            return (
                              <tr key={inv?.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                  <p className="font-medium text-gray-900 text-sm">{email}</p>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-700 font-semibold text-xs">
                                    {inv?.quotes_count}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-right font-semibold text-gray-900">
                                  {formatEuro(inv?.amount_cents)}
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <StatusBadge status={inv?.status} />
                                </td>
                                <td className="px-4 py-4 text-center text-gray-500 text-xs">
                                  {formatDate(inv?.paid_at)}
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <button
                                      onClick={() => { setDrawerInvoice(inv); setDrawerEmail(email); }}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="Visualizza dettaglio"
                                    >
                                      <Eye size={15} />
                                    </button>
                                    {canRefund && (
                                      <button
                                        onClick={() => setRefundInvoice(inv)}
                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Rimborsa"
                                      >
                                        <RefreshCw size={15} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Esenzioni fatturazione ── */}
        <EsenzioniSection showToast={showToast} />

      </div>
      {/* Detail Drawer */}
      {drawerInvoice && (
        <DetailDrawer
          invoice={drawerInvoice}
          professionalEmail={drawerEmail}
          onClose={() => { setDrawerInvoice(null); setDrawerEmail(null); }}
          onRefund={() => { setRefundInvoice(drawerInvoice); }}
        />
      )}
      {/* Refund Modal */}
      {refundInvoice && (
        <RefundModal
          invoice={refundInvoice}
          onClose={() => setRefundInvoice(null)}
          onSuccess={handleRefundSuccess}
        />
      )}
      {/* Toast */}
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
    </AdminDashboardLayout>
  );
};

export default AdminFatturazione;
